import { useRef, useState } from 'react';
import { Palette, Sun, Moon, Check } from 'lucide-react';
import { useTheme, themeConfigs, type ColorTheme } from '@kss/theme';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../utils/cn';

export function ThemePicker({ className }: { className?: string }) {
  const { mode, colorTheme, setMode, setColorTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Theme"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
      >
        <Palette className="h-4 w-4" />
      </button>
      {open && (
        // Open upward (bottom-full) instead of downward — the picker is
        // typically rendered in a sticky footer where downward expansion
        // overflows below the viewport on mobile. Right-edge alignment
        // is clamped via `max-w-[calc(100vw-1rem)]` so narrow phones
        // can't push the panel off-screen horizontally either.
        <div className="absolute right-0 bottom-full z-50 mb-2 w-60 max-w-[calc(100vw-1rem)] rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 shadow-xl">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Mode
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('light')}
              className={cn(
                'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors',
                mode === 'light'
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]'
              )}
            >
              <Sun className="h-3.5 w-3.5" /> Light
            </button>
            <button
              type="button"
              onClick={() => setMode('dark')}
              className={cn(
                'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors',
                mode === 'dark'
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]'
              )}
            >
              <Moon className="h-3.5 w-3.5" /> Dark
            </button>
          </div>

          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Accent
          </div>
          <div className="grid grid-cols-3 gap-2">
            {themeConfigs.map((t) => {
              const active = colorTheme === t.name;
              return (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => setColorTheme(t.name as ColorTheme)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-md border p-2 text-[11px] font-medium transition-colors',
                    active
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]'
                  )}
                >
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full"
                    style={{ backgroundColor: t.previewColor }}
                  >
                    {active && <Check className="h-3.5 w-3.5 text-white" />}
                  </span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ThemePicker;
